import { useMemo, useState } from 'react'
import { Button, Input, Modal, Select } from '@/components/ui'
import type { CentralSubscription, SubscriptionPlan, SubscriptionStatus } from '@/api/whatsapp'

interface SubscriptionFormProps {
  mode: 'add' | 'edit'
  initial?: Partial<CentralSubscription>
  onSave: (data: Partial<CentralSubscription>) => Promise<void>
  onClose: () => void
}

function toDateInputValue(value?: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 10)
}

export default function SubscriptionForm({ mode, initial, onSave, onClose }: SubscriptionFormProps) {
  const [orgId, setOrgId] = useState(initial?.org_id ?? '')
  const [organizationName, setOrganizationName] = useState(initial?.organization_name ?? '')
  const [userEmail, setUserEmail] = useState(initial?.user_email ?? '')
  const [plan, setPlan] = useState<SubscriptionPlan>((initial?.plan as SubscriptionPlan) ?? 'basic')
  const [status, setStatus] = useState<SubscriptionStatus>((initial?.status as SubscriptionStatus) ?? 'active')
  const [expiresAt, setExpiresAt] = useState(toDateInputValue(initial?.expires_at))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const title = useMemo(() => (mode === 'add' ? 'Add Subscription' : 'Edit Subscription'), [mode])

  const submit = async () => {
    if (!orgId.trim() || !organizationName.trim() || !userEmail.trim()) {
      setError('Org ID, organization name and user email are required.')
      return
    }

    setSaving(true)
    setError(null)
    try {
      await onSave({
        org_id: orgId.trim(),
        organization_name: organizationName.trim(),
        user_email: userEmail.trim(),
        plan,
        status,
        expires_at: expiresAt ? new Date(`${expiresAt}T00:00:00.000Z`).toISOString() : null,
      })
      onClose()
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to save subscription.'
      setError(message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={title}
      size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} loading={saving}>
            {mode === 'add' ? 'Create' : 'Save Changes'}
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <Input
          label="Org ID"
          placeholder="acme-corp"
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          required
          disabled={mode === 'edit'}
        />
        <Input
          label="Organization Name"
          value={organizationName}
          onChange={(e) => setOrganizationName(e.target.value)}
          required
        />
        <Input
          label="User Email"
          value={userEmail}
          onChange={(e) => setUserEmail(e.target.value)}
          required
          type="email"
        />
        <Select
          label="Plan"
          value={plan}
          onChange={(e) => setPlan(e.target.value as SubscriptionPlan)}
          options={[
            { value: 'basic', label: 'Basic' },
            { value: 'pro', label: 'Pro' },
            { value: 'enterprise', label: 'Enterprise' },
          ]}
        />
        <Select
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value as SubscriptionStatus)}
          options={[
            { value: 'active', label: 'Active' },
            { value: 'trial', label: 'Trial' },
            { value: 'cancelled', label: 'Cancelled' },
            { value: 'expired', label: 'Expired' },
          ]}
        />
        <Input
          label="Expires At"
          type="date"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
        />
        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Modal>
  )
}
